import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Tooltip,
  TableContainer,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import Papa from "papaparse";
import {
  saveDataset,
  getDataset,
} from "../../../../../../utils/IndexedDBDexieUtility";
import { tableStyles } from "../../../../../../Constants/tableStyles";
import CustomInput from "../../../../../../Components/Input/CustomInput";
import {
  deleteButtonStyles,
  primaryButtonStyles,
} from "../../../../../../Constants/buttonStyles";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import theme from "../../../../../../Constants/theme";

const FinetuningDataset = ({
  nodeId,
  botData,
  setBotData,
  showSnackbar,
  LLMFinetuning,
}) => {
  const [csvData, setCsvData] = useState({});
  const [dataset, setDataset] = useState(
    botData?.finetuning_dataset[0]?.name || ""
  );
  const [openDatasetModal, setOpenDatasetModal] = useState(false);
  const [resetInput, setResetInput] = useState(false); // For resetting the file input
  const inputRef = useRef(null);

  useEffect(() => {
    // Load datasets from IndexedDB and update state
    const loadData = async () => {
      try {
        const storedDatasets = await getDataset(`finetuning_dataset_${nodeId}`);
        const storedCSV = await getDataset(`csvData_${nodeId}`);
        if (storedDatasets) setCsvData(storedDatasets);
        if (storedCSV) setCsvData(storedCSV);
      } catch (error) {
        console.error("Error loading dataset: ", error);
      }
    };

    loadData();
  }, [nodeId]);

  const handleUploadFinetuningDataset = (e) => {
    const files = e.target.files;
    let fileValidation = false;

    if (!files) return;
    setResetInput(true);
    setTimeout(() => setResetInput(false), 100);

    for (const file of files) {
      const reader = new FileReader();
      const size = file.size / (1024 * 1024); // Convert size to MB

      if (
        file.type !== "text/csv" &&
        file.type !==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        showSnackbar(
          `${file.type} is not a supported file type. Supported file types: CSV, XLSX`,
          "error"
        );
        fileValidation = true;
        continue;
      }

      Papa.parse(file, {
        complete: (result) => {
          if (fileValidation) return;

          const fileData = result.data;
          const systemPrompt = "system_prompt";
          const query = "query";
          const response = "response";
          const lengthOfEntries = fileData.length - 1;

          if (lengthOfEntries !== 0 && lengthOfEntries < 10) {
            showSnackbar(
              "Dataset must contain at least 10 entries for fine-tuning.",
              "error"
            );
            fileValidation = true;
            return;
          }

          if (
            !fileData[0]?.[systemPrompt] ||
            !fileData[0]?.[query] ||
            !fileData[0]?.[response]
          ) {
            showSnackbar(
              `The CSV file must start with the columns "${systemPrompt}", "${query}", and "${response}".`,
              "error"
            );
            fileValidation = true;
            return;
          }

          setCsvData((prevData) => ({ ...prevData, [file.name]: fileData }));
          saveDataset(`csvData${nodeId}`, {
            ...csvData,
            [file.name]: fileData,
          });
        },
        header: true,
      });

      reader.onload = () => {
        if (fileValidation) return;

        setBotData((prevData) => ({
          ...prevData,
          finetuning_dataset: [
            ...(Array.isArray(prevData.finetuning_dataset)
              ? prevData.finetuning_dataset
              : []),
            {
              filedata: reader.result,
              id: Date.now() + "",
              fileName: file.name,
              fileType: file.type,
              creationDate: Date.now(),
              category: "",
              fileSize: size,
            },
          ],
        }));

        setDataset(file.name);
        localStorage.setItem(`dataset_${nodeId}`, JSON.stringify(file.name));
        saveDataset(`finetuning_dataset_${nodeId}`, [
          ...(botData.finetuning_dataset || []),
          {
            filedata: reader.result,
            id: Date.now() + "",
            fileName: file.name,
            fileType: file.type,
            creationDate: Date.now(),
            category: "",
            fileSize: size,
          },
        ]);

        showSnackbar("Dataset uploaded successfully!", "success");
      };

      if (file.type === "text/csv") {
        reader.readAsText(file);
      } else if (
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        reader.readAsArrayBuffer(file);
      }
    }
  };

  const handleStructuredFileRemove = (fileNameToRemove) => {
    setBotData((prevData) => ({
      ...prevData,
      finetuning_dataset: prevData.finetuning_dataset.filter(
        (dataset) => dataset.fileName !== fileNameToRemove
      ),
    }));

    setCsvData((prevCsvData) => {
      const updatedCsvData = { ...prevCsvData };
      delete updatedCsvData[fileNameToRemove];
      return updatedCsvData;
    });

    if (dataset === fileNameToRemove) {
      setDataset(
        botData?.finetuning_dataset?.length > 0
          ? botData.finetuning_dataset[0].fileName
          : ""
      );
    }

    setResetInput(true);
    setTimeout(() => setResetInput(false), 100);
  };

  return (
    <>
      <Tooltip title="The CSV file must start with the columns 'system_prompt', 'query' and 'response' in that order.">
        <CustomInput
          inputRef={inputRef}
          resetInput={resetInput}
          placeholder={"Upload CSV/Excel files"}
          type={"file"}
          upload_type={"finetuned"}
          multiple={true}
          onHandleChange={handleUploadFinetuningDataset}
          disable={!LLMFinetuning}
        />{" "}
      </Tooltip>
      <Button
        onClick={() => setOpenDatasetModal(true)}
        sx={primaryButtonStyles}
        disabled={!LLMFinetuning}
      >
        View Datasets
      </Button>

      {/* Fine Tuning Dataset Dialog */}
      <Dialog
        fullWidth
        maxWidth={"lg"}
        open={openDatasetModal}
        onClose={() => setOpenDatasetModal(false)}
        PaperProps={{ sx: { ...dialogStyles.dialogRoot } }}
      >
        <DialogTitle sx={dialogStyles.dialogTitle}>
          <Typography sx={dialogStyles.dialogTitleText}>
            Fine-tuning Dataset
          </Typography>
          <IconButton
            sx={dialogStyles.dialogCloseIcon}
            onClick={() => setOpenDatasetModal(false)}
            size="small"
          >
            <Clear />
          </IconButton>
        </DialogTitle>
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={() => handleStructuredFileRemove(dataset)}
            sx={deleteButtonStyles}
          >
            Delete dataset
          </Button>
        </DialogActions>
        <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
          {Object.keys(csvData).length === 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: theme.main.extremeWhite,
                width: "100%",
              }}
            >
              <Typography variant="body1" textAlign={"center"}>
                No data uploaded for fine-tuning
              </Typography>
            </Box>
          )}
          {Object.keys(csvData).length > 0 && (
            <TableContainer sx={tableStyles.tableContainer}>
              <Table>
                <TableHead sx={tableStyles.tableHead}>
                  {csvData[dataset] && csvData[dataset].length > 0 && (
                    <TableRow sx={tableStyles.tableRow}>
                      {csvData[dataset] &&
                        (Object.keys(csvData[dataset][0]) || [])?.map(
                          (header, index) => (
                            <TableCell
                              sx={tableStyles.tableHeaderCell}
                              key={index}
                            >
                              {header}
                            </TableCell>
                          )
                        )}
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {csvData[dataset]?.map(
                    (row, rowIndex) =>
                      rowIndex !== 0 && (
                        <TableRow key={rowIndex} sx={tableStyles.tableRow}>
                          {Object?.values(row)?.map((value, cellIndex) => (
                            <TableCell
                              sx={tableStyles.tableCell}
                              key={cellIndex}
                            >
                              {value.length < 100
                                ? value
                                : value.substring(0, 100) + "..."}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinetuningDataset;
